import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Calendar, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_LABEL = {
  everyone: "Everyone",
  couples: "Couples",
  tv: "TV",
  single_guys: "Single Guys",
  single_ladies: "Single Ladies",
};

const fmtDate = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

export default function TicketPage() {
  const { ticketId } = useParams();
  const [params] = useSearchParams();
  const walletHint = params.get("wallet"); // "apple" | "google" — from wallet buttons in email

  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tickets/${ticketId}`);
        setData(data);
        setState("ok");
      } catch {
        setState("notfound");
      }
    })();
  }, [ticketId]);

  useEffect(() => {
    if (walletHint === "apple" || walletHint === "google") {
      toast(
        walletHint === "apple"
          ? "Apple Wallet passes coming soon — for now, screenshot this page."
          : "Google Wallet passes coming soon — for now, screenshot this page."
      );
    }
  }, [walletHint]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
      </div>
    );
  }

  if (state === "notfound" || !data) {
    return (
      <div className="min-h-screen bg-black text-[#F5F5F0] flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Ticket not found</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter">This ticket doesn't exist.</h1>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            Home
          </Link>
        </div>
      </div>
    );
  }

  const { ticket, event, venue } = data;
  const address = venue.address || {};
  const addressLine = [address.line1, address.city, address.postcode].filter(Boolean).join(", ");

  const openWallet = (which) => {
    toast(
      which === "apple"
        ? "Apple Wallet passes coming soon."
        : "Google Wallet passes coming soon.",
      { description: "This ticket will still scan at the door from this page." }
    );
  };

  return (
    <div className="min-h-screen bg-black text-[#F5F5F0] py-6 px-4 md:py-12" data-testid="ticket-page">
      <div className="max-w-md mx-auto">
        {/* Venue header */}
        <div className="text-center pb-6 border-b border-white/10">
          {venue.logo_data_uri ? (
            <img
              src={venue.logo_data_uri}
              alt={venue.name}
              className="h-14 max-w-[180px] object-contain mx-auto"
            />
          ) : (
            <div className="text-2xl font-black tracking-tighter">{venue.name}</div>
          )}
          <div className="mt-3 text-xs uppercase tracking-[0.28em] text-[#D9A6F5]">
            {ticket.status === "used" ? "Ticket already used" : "Valid ticket"}
          </div>
        </div>

        {/* Ticket body */}
        <div className="mt-6 bg-[#0A0A0A] border border-white/10">
          <div className="px-6 pt-6 pb-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-2">
              {(AUDIENCE_LABEL[ticket.tier] || ticket.tier).toUpperCase()} · Admits 1
            </div>
            <h1
              data-testid="ticket-event-name"
              className="text-2xl md:text-3xl font-black tracking-tighter leading-[1.05]"
            >
              {event.name}
            </h1>
            <div className="mt-4 space-y-2 text-sm text-[#A3A3A3]">
              <div className="inline-flex items-center gap-2">
                <Calendar size={14} className="text-[#D9A6F5]" />
                <span data-testid="ticket-event-date">
                  {fmtDate(event.first_date)} · {event.time}
                </span>
              </div>
              {addressLine && (
                <div className="inline-flex items-center gap-2">
                  <MapPin size={14} className="text-[#D9A6F5]" />
                  <span>{addressLine}</span>
                </div>
              )}
            </div>
          </div>

          {/* QR code */}
          <div className="px-6 py-6 flex flex-col items-center">
            <div className="bg-white p-3">
              <img
                src={ticket.qr_data_uri}
                alt="Ticket QR"
                data-testid="ticket-qr"
                className="w-56 h-56"
              />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3]">
              Present at the door
            </div>
            <div className="mt-2 text-[10px] font-mono text-[#666] break-all text-center">
              {ticket.id}
            </div>
          </div>

          {/* Holder */}
          <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between text-xs">
            <div className="text-[#A3A3A3] uppercase tracking-[0.24em]">Holder</div>
            <div className="text-[#F5F5F0]" data-testid="ticket-holder">
              {ticket.holder_first_name} {ticket.holder_last_name}
            </div>
          </div>
        </div>

        {/* Wallet buttons */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="ticket-apple-wallet"
            onClick={() => openWallet("apple")}
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-3 text-[10px] uppercase tracking-[0.24em] font-bold transition-colors"
          >
            <AppleGlyph /> Apple Wallet
          </button>
          <button
            type="button"
            data-testid="ticket-google-wallet"
            onClick={() => openWallet("google")}
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-3 text-[10px] uppercase tracking-[0.24em] font-bold transition-colors"
          >
            <GoogleGlyph /> Google Wallet
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#A3A3A3]">
          <ShieldCheck size={11} className="text-[#D9A6F5]" /> Do not share this page — QR is
          single-use
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 text-center text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3]">
          Powered by <Link to="/" className="text-[#D9A6F5]">Swingle</Link>
        </div>
      </div>
    </div>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M16.365 12.9c-.02-2.03 1.66-3.005 1.74-3.055-.945-1.38-2.42-1.57-2.945-1.59-1.255-.13-2.45.74-3.085.74-.635 0-1.62-.72-2.66-.7-1.37.02-2.635.795-3.34 2.02-1.42 2.47-.365 6.13 1.02 8.14.68.98 1.485 2.08 2.545 2.04 1.02-.04 1.405-.66 2.64-.66 1.235 0 1.585.66 2.665.64 1.1-.02 1.795-1 2.47-1.98.775-1.14 1.095-2.24 1.115-2.3-.025-.01-2.14-.82-2.16-3.25zM14.31 6.36c.565-.685.94-1.635.835-2.585-.81.035-1.79.54-2.375 1.225-.525.605-.985 1.575-.86 2.505.9.07 1.83-.46 2.4-1.145z"/>
    </svg>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path fill="#4285F4" d="M22 12.2c0-.7-.06-1.36-.17-2H12v3.8h5.6c-.24 1.28-.98 2.36-2.08 3.08v2.55h3.36c1.97-1.82 3.12-4.5 3.12-7.43z"/>
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.42l-3.36-2.55c-.93.62-2.12.99-3.26.99-2.5 0-4.62-1.7-5.38-3.98H2.98v2.6C4.65 19.92 8.05 22 12 22z"/>
      <path fill="#FBBC05" d="M6.62 14.04A5.99 5.99 0 016.3 12c0-.72.13-1.42.32-2.04V7.36H2.98A9.99 9.99 0 002 12c0 1.62.4 3.15 1.08 4.49l3.54-2.45z"/>
      <path fill="#EA4335" d="M12 6.02c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 3 14.7 2 12 2 8.05 2 4.65 4.08 2.98 7.36l3.64 2.6C7.38 7.72 9.5 6.02 12 6.02z"/>
    </svg>
  );
}
